import React from 'react';
import { Link } from 'react-router-dom';
import '../monitoring/Monitoring.css';
import './Help.css';

const CONCEPTS = [
  {
    icon: 'fa-project-diagram',
    color: '#16a34a',
    title: 'Projects',
    description: "Each project is a site or engagement you're tracking - set its location, type, and which compliance categories apply to it.",
  },
  {
    icon: 'fa-compass',
    color: '#0891b2',
    title: 'Compliance Tracking (categories)',
    description: 'What your organisation actually monitors - OSH, Environmental, Labor, Stakeholder Engagement, GBV, and more, plus any custom category you add. Set this up once in Settings and it tailors your dashboard and new projects.',
  },
  {
    icon: 'fa-clipboard-check',
    color: '#3b82f6',
    title: 'Inspections',
    description: 'Structured, checklist-based site visits against a project and category. Any item marked non-compliant automatically raises a Compliance Finding, and typed readings feed into Monitoring trends.',
  },
  {
    icon: 'fa-exclamation-triangle',
    color: '#dc2626',
    title: 'Risks & Issues (Compliance Findings)',
    description: "Anything that needs a corrective action tracked to closure - raised automatically from inspections or logged manually. Each has a severity (Critical/Major/Minor), a status, and a deadline.",
  },
  {
    icon: 'fa-circle',
    color: '#f59e0b',
    title: 'Compliance Score & RAG',
    description: "A 0-100% score blending two things - how compliant your latest Inspections found things to be, and how well the issues they (or you) raised are being resolved - plus a Green/Amber/Red rating. The rating always matches your worst open finding - a project can't show Red if nothing raised is actually Red.",
  },
  {
    icon: 'fa-file-contract',
    color: '#84cc16',
    title: 'EIA Certificates & Conditions',
    description: "For organisations with a formal Environmental Impact Assessment certificate - register it, define the specific conditions you must monitor (e.g. a noise limit, checked monthly), and generate recurring monitoring tasks automatically.",
  },
  {
    icon: 'fa-chart-line',
    color: '#8b5cf6',
    title: 'Monitoring (Parameters, Daily Log, Trends)',
    description: "Configure what you want to measure and how often (a Monitoring Parameter), log day-to-day readings against it (Daily Log), and review how those readings change over time (Trends).",
  },
  {
    icon: 'fa-chart-bar',
    color: '#0891b2',
    title: 'Reports',
    description: 'Compliance reports, the findings register, incidents, and achievements - filterable by project, category, and date range, ready to export or share.',
  },
  {
    icon: 'fa-users',
    color: '#374151',
    title: 'Team & Roles',
    description: "Invite colleagues with an access level (Admin, Manager, Contributor, Viewer) that controls what they can do, and a descriptive job role (e.g. Environmental Officer, Field Inspector) for context.",
  },
];

const STEPS = [
  { title: 'Set up what you track', description: 'Go to Settings > Compliance Tracking and choose the categories your organisation needs to monitor (and whether you have a formal EIA certificate).' },
  { title: 'Create a project', description: "Add the site or engagement you're tracking, and pick which categories apply to it." },
  { title: 'Configure or inspect', description: 'Set up Monitoring Parameters for ongoing readings, and/or conduct Inspections against a checklist.' },
  { title: 'Track issues to closure', description: 'Anything non-compliant becomes a Risk/Issue with a severity and deadline - work it through to Done.' },
  { title: 'Report on progress', description: 'Use Reports to see compliance trends, the findings register, and incidents across your projects.' },
];

const Help = () => (
  <div className="monitoring-page help-page">
    <div className="monitoring-header">
      <div>
        <h1><i className="fas fa-question-circle"></i> Help & Getting Started</h1>
        <p className="header-subtitle">What this platform does, and how the pieces fit together</p>
      </div>
    </div>

    <div className="help-intro">
      <h2>What is this?</h2>
      <p>
        This is a compliance monitoring platform for organisations running projects that need ongoing oversight -
        across whatever areas matter to you. That can include environmental compliance, labor conditions and
        occupational health & safety, stakeholder engagement, GBV/SEA prevention, biodiversity, cultural heritage,
        permits, and formal EIA (Environmental Impact Assessment) conditions where applicable. You choose which of
        these your organisation actually needs to track, and the app tailors itself accordingly.
      </p>
    </div>

    <h3 className="help-section-title">Key concepts</h3>
    <div className="help-concepts-grid">
      {CONCEPTS.map((concept) => (
        <div key={concept.title} className="help-concept-card">
          <div className="help-concept-icon" style={{ backgroundColor: `${concept.color}15`, color: concept.color }}>
            <i className={`fas ${concept.icon}`}></i>
          </div>
          <div>
            <h4>{concept.title}</h4>
            <p>{concept.description}</p>
          </div>
        </div>
      ))}
    </div>

    <h3 className="help-section-title">Getting started</h3>
    <div className="help-steps">
      {STEPS.map((step, index) => (
        <div key={step.title} className="help-step">
          <div className="help-step-number">{index + 1}</div>
          <div>
            <h4>{step.title}</h4>
            <p>{step.description}</p>
          </div>
        </div>
      ))}
    </div>

    <div className="help-footer-note">
      <p>
        Need to change what your organisation tracks? Head to <Link to="/settings">Settings</Link> at any time.
      </p>
    </div>
  </div>
);

export default Help;
